import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

type ContactMethod = 'form' | 'email' | 'twitter' | 'discord';

const contactMethods = [
  {
    id: 'form' as ContactMethod,
    icon: 'ri-mail-send-line',
    label: 'お問い合わせフォーム',
    description: '最も確実な方法。内容に応じて担当者が対応します。',
    badge: '推奨',
    badgeColor: '#00d9ff',
    color: '#00d9ff',
  },
  {
    id: 'email' as ContactMethod,
    icon: 'ri-mail-line',
    label: 'メール',
    description: 'support@dignia.app 宛にご連絡ください。',
    badge: null,
    color: '#00ff41',
    href: 'mailto:support@dignia.app',
  },
  {
    id: 'twitter' as ContactMethod,
    icon: 'ri-twitter-x-line',
    label: 'X (Twitter)',
    description: '@dignia_app にDMまたはメンションでご連絡ください。',
    badge: null,
    color: '#ffffff',
    href: 'https://twitter.com/dignia_app',
  },
  {
    id: 'discord' as ContactMethod,
    icon: 'ri-discord-line',
    label: 'Discord',
    description: 'コミュニティサーバーのサポートチャンネルへ。',
    badge: null,
    color: '#ff006e',
    href: 'https://discord.gg/dignia',
  },
];

const categories = [
  { value: '', label: 'カテゴリを選択してください' },
  { value: 'account', label: 'アカウント・ログインについて' },
  { value: 'payment', label: '決済・課金について' },
  { value: 'nfc', label: 'NFC・チェックインについて' },
  { value: 'artist', label: 'アーティスト機能について' },
  { value: 'node', label: 'NODE（店舗）機能について' },
  { value: 'bug', label: '不具合・バグの報告' },
  { value: 'feature', label: '機能のご要望' },
  { value: 'other', label: 'その他' },
];

export default function ContactPage() {
  const navigate = useNavigate();
  const [activeMethod, setActiveMethod] = useState<ContactMethod>('form');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;

    setSubmitStatus('loading');
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((value, key) => {
      body.append(key, value.toString());
    });

    const name = (form.elements.namedItem('name') as HTMLInputElement)?.value || '';
    const email = (form.elements.namedItem('email') as HTMLInputElement)?.value || '';
    const category = (form.elements.namedItem('category') as HTMLSelectElement)?.value || '';

    try {
      const res = await fetch('https://readdy.ai/api/form/d6li1aiqoe30lj0v80f0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        // Supabaseにお問い合わせデータを保存（失敗してもサイレントに無視）
        try {
          await supabase.from('contact_inquiries').insert({
            name,
            email,
            category,
            message,
            status: 'pending',
          });
        } catch {
          // INSERT失敗はサイレントに無視
        }

        // 自動返信メール送信
        try {
          await fetch('https://ssagkhacsdsygkcojouo.supabase.co/functions/v1/send-contact-auto-reply', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name, email, category, message }),
          });
        } catch {
          // 自動返信失敗はサイレントに無視（フォーム送信自体は成功扱い）
        }
        setSubmitStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white pb-24">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
            <span className="text-sm font-medium">BACK</span>
          </button>
          <h1 className="text-xl font-bold tracking-wider">
            <span className="bg-gradient-to-r from-cyan-400 via-pink-400 to-yellow-400 bg-clip-text text-transparent">
              CONTACT
            </span>
          </h1>
          <div className="w-16"></div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8 space-y-8">

        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center"
        >
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{
              background: 'linear-gradient(135deg, rgba(0,217,255,0.2), rgba(255,0,110,0.2))',
              border: '1px solid rgba(255,255,255,0.15)',
            }}
          >
            <i className="ri-customer-service-2-line text-3xl text-white/80"></i>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">お問い合わせ</h2>
          <p className="text-white/50 text-sm leading-relaxed">
            ご不明な点・ご要望・不具合のご報告など、<br />
            お気軽にご連絡ください。
          </p>
        </motion.div>

        {/* Contact Method Selector */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <p className="text-xs font-bold tracking-widest text-white/40 mb-3">お問い合わせ方法を選択</p>
          <div className="grid grid-cols-2 gap-3">
            {contactMethods.map((method) => {
              const isActive = activeMethod === method.id;
              return (
                <button
                  key={method.id}
                  onClick={() => setActiveMethod(method.id)}
                  className="relative text-left p-4 rounded-xl border transition-all cursor-pointer"
                  style={{
                    backgroundColor: isActive ? `${method.color}10` : 'rgba(255,255,255,0.03)',
                    borderColor: isActive ? `${method.color}50` : 'rgba(255,255,255,0.08)',
                    boxShadow: isActive ? `0 0 20px ${method.color}20` : 'none',
                  }}
                >
                  {method.badge && (
                    <span
                      className="absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: `${method.badgeColor}20`,
                        color: method.badgeColor,
                        border: `1px solid ${method.badgeColor}40`,
                      }}
                    >
                      {method.badge}
                    </span>
                  )}
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center mb-3"
                    style={{
                      backgroundColor: `${method.color}15`,
                      border: `1px solid ${method.color}30`,
                    }}
                  >
                    <i className={`${method.icon} text-lg`} style={{ color: method.color }}></i>
                  </div>
                  <p className="text-white text-sm font-semibold mb-1">{method.label}</p>
                  <p className="text-white/40 text-xs leading-relaxed">{method.description}</p>
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Content Area */}
        <motion.div
          key={activeMethod}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {activeMethod === 'form' && (
            <div
              className="rounded-2xl border border-white/10 p-6"
              style={{ background: 'rgba(255,255,255,0.03)' }}
            >
              <p className="text-xs font-bold tracking-widest text-white/40 mb-5">お問い合わせフォーム</p>

              {submitStatus === 'success' ? (
                <div className="text-center py-10">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: 'rgba(0,255,65,0.15)', border: '1px solid rgba(0,255,65,0.4)' }}
                  >
                    <i className="ri-check-line text-3xl" style={{ color: '#00ff41' }}></i>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">送信完了しました</h3>
                  <p className="text-white/50 text-sm leading-relaxed mb-6">
                    お問い合わせありがとうございます。<br />
                    通常2〜3営業日以内にご返信いたします。
                  </p>
                  <button
                    onClick={() => setSubmitStatus('idle')}
                    className="px-6 py-2.5 rounded-xl text-sm font-bold tracking-wider cursor-pointer transition-all hover:scale-105"
                    style={{
                      background: 'rgba(0,217,255,0.15)',
                      border: '1px solid rgba(0,217,255,0.4)',
                      color: '#00d9ff',
                    }}
                  >
                    別の内容を送る
                  </button>
                </div>
              ) : (
                <form
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  {/* Name */}
                  <div>
                    <label className="block text-xs font-bold tracking-wider text-white/50 mb-2">
                      お名前 <span className="text-pink-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="山田 太郎"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-cyan-400/50 transition-colors"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-xs font-bold tracking-wider text-white/50 mb-2">
                      メールアドレス <span className="text-pink-400">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="your@email.com"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-cyan-400/50 transition-colors"
                    />
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-xs font-bold tracking-wider text-white/50 mb-2">
                      カテゴリ <span className="text-pink-400">*</span>
                    </label>
                    <select
                      name="category"
                      required
                      className="w-full bg-[#111] border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-cyan-400/50 transition-colors cursor-pointer"
                    >
                      {categories.map((cat) => (
                        <option key={cat.value} value={cat.value} disabled={cat.value === ''}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-xs font-bold tracking-wider text-white/50 mb-2">
                      お問い合わせ内容 <span className="text-pink-400">*</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="お問い合わせ内容を詳しくご記入ください..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-cyan-400/50 transition-colors resize-none"
                    />
                    <div className="flex justify-end mt-1">
                      <span className={`text-xs ${charCount > 480 ? 'text-pink-400' : 'text-white/30'}`}>
                        {charCount} / 500
                      </span>
                    </div>
                  </div>

                  {submitStatus === 'error' && (
                    <p className="text-pink-400 text-xs text-center">
                      送信に失敗しました。時間をおいて再度お試しください。
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitStatus === 'loading' || charCount > 500}
                    className="w-full py-3.5 rounded-xl font-bold text-sm tracking-wider transition-all cursor-pointer hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
                    style={{
                      background: 'linear-gradient(135deg, rgba(0,217,255,0.3), rgba(255,0,110,0.3))',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: '#ffffff',
                    }}
                  >
                    {submitStatus === 'loading' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        送信中...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>
                        送信する
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          )}

          {activeMethod !== 'form' && (
            <div
              className="rounded-2xl border border-white/10 p-8 text-center"
              style={{ background: 'rgba(255,255,255,0.03)' }}
            >
              {(() => {
                const method = contactMethods.find((m) => m.id === activeMethod)!;
                return (
                  <>
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
                      style={{
                        backgroundColor: `${method.color}15`,
                        border: `1px solid ${method.color}30`,
                      }}
                    >
                      <i className={`${method.icon} text-3xl`} style={{ color: method.color }}></i>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{method.label}</h3>
                    <p className="text-white/50 text-sm leading-relaxed mb-6">{method.description}</p>
                    <a
                      href={method.href}
                      target={activeMethod !== 'email' ? '_blank' : undefined}
                      rel="noopener noreferrer nofollow"
                      className="inline-flex items-center gap-2 px-8 py-3 rounded-xl font-bold text-sm tracking-wider transition-all cursor-pointer hover:scale-105 active:scale-95 whitespace-nowrap"
                      style={{
                        backgroundColor: `${method.color}15`,
                        border: `1px solid ${method.color}40`,
                        color: method.color,
                      }}
                    >
                      <i className={`${method.icon} text-base`}></i>
                      {activeMethod === 'email' && 'メールを送る'}
                      {activeMethod === 'twitter' && 'Xを開く'}
                      {activeMethod === 'discord' && 'Discordに参加する'}
                    </a>
                  </>
                );
              })()}
            </div>
          )}
        </motion.div>

        {/* Response Time Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="rounded-xl border border-white/8 p-5"
          style={{ background: 'rgba(255,255,255,0.02)' }}
        >
          <p className="text-xs font-bold tracking-widest text-white/30 mb-4">対応時間・目安</p>
          <div className="space-y-3">
            {[
              { icon: 'ri-time-line', label: 'フォーム・メール', value: '2〜3営業日以内', color: '#00d9ff' },
              { icon: 'ri-twitter-x-line', label: 'X (Twitter)', value: '1〜2営業日以内', color: '#ffffff' },
              { icon: 'ri-discord-line', label: 'Discord', value: '当日〜翌営業日', color: '#ff006e' },
            ].map((item) => (
              <div key={item.label} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className={`${item.icon} text-sm`} style={{ color: item.color }}></i>
                  <span className="text-white/50 text-xs">{item.label}</span>
                </div>
                <span className="text-white/70 text-xs font-medium">{item.value}</span>
              </div>
            ))}
          </div>
        </motion.div>

      </div>
    </div>
  );
}
